/**
 * Modeville <mv-gallery>: one large photo, thumbnails, colour filter, one-second autoplay.
 * Autoplay runs until the visitor explicitly picks a colour or navigates by hand, then stays stopped.
 * It only previews photos (never changes the chosen variant); a caption names the colour shown vs selected.
 * No visible pause button (keyboard-only control on focus). Reduced motion: no autoplay.
 */
(() => {
  'use strict';
  if (window.MvGalleryBase) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SIZES = '(min-width: 750px) 640px, 100vw';

  const sized = (src, width) =>
    /[?&]width=\d+/.test(src)
      ? src.replace(/([?&])width=\d+/, `$1width=${width}`)
      : `${src}${src.includes('?') ? '&' : '?'}width=${width}`;

  const srcset = (src) => [600, 900, 1200, 1600].map((w) => `${sized(src, w)} ${w}w`).join(', ');

  class MvGallery extends HTMLElement {
    connectedCallback() {
      if (!this._init && !this.setup()) return;
      this.attachDocument();
      if (this.playing) this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.timer = 0;
      this.detachDocument();
    }

    setup() {
      const dataEl = this.querySelector('script[data-mv-gallery-data]');
      if (!dataEl) return false;
      try {
        this.items = JSON.parse(dataEl.textContent || '[]');
      } catch (error) {
        return false;
      }
      if (!this.items.length) return false;
      this._init = true;

      this.intervalMs = Number(this.dataset.autoplayMs) || 1000;
      this.colourOption = (this.dataset.colourOption || '').trim().toLowerCase();
      this.productId = this.dataset.productId || '';
      this.zoomEnabled = this.dataset.zoom !== 'false';

      this.stage = this.querySelector('[data-stage]');
      this.frame = this.querySelector('[data-frame]');
      this.layers = Array.from(this.querySelectorAll('[data-layer]'));
      this.thumbsBox = this.querySelector('[data-thumbs]');
      this.thumbItems = new Map(Array.from(this.querySelectorAll('[data-thumb-item]')).map((el) => [Number(el.dataset.id), el]));
      this.playButton = this.querySelector('[data-play]');
      this.status = this.querySelector('[data-status]');
      this.caption = this.querySelector('[data-caption]');
      this.dialog = this.querySelector('[data-dialog]');
      this.zoomScroller = this.querySelector('[data-zoom-scroller]');
      this.zoomImg = this.querySelector('[data-zoom-img]');

      this.colour = null;
      this.chosenColour = '';
      this.playing = false;
      this.timer = 0;
      this.token = 0;
      this.activeLayer = 0;
      this.drag = null;
      this.lastDragAt = 0;
      this.preloaded = new Set();

      this.byId = new Map(this.items.map((item) => [item.id, item]));
      this.current = this.byId.get(Number(this.dataset.firstId)) || this.items[0];

      this.bindInternal();

      const chosen = (this.dataset.chosenColour || '').trim();
      if (chosen) {
        this.chooseColour(chosen);
      } else {
        this.applyVisibility();
        this.setAttribute('data-current-id', String(this.current.id));
        this.updateCaption();
        if (!this.startAutoplay()) {
          this.setAttribute('data-mode', 'manual');
          this.setAttribute('data-playing', 'false');
        }
      }
      this.preloadAhead();
      this.updateControls();
      return true;
    }

    /* ---------------------------------------------------------------- wiring */

    attachDocument() {
      if (this._docBound) return;
      this._docBound = true;
      this.onDocEvent = (event) => this.handleColourEvent(event);
      this.onVisibility = () => {
        if (!document.hidden && this.playing) this.schedule();
      };
      this.onMotionChange = () => {
        if (reducedMotion.matches) this.stopAutoplay();
        this.updateControls();
      };
      document.addEventListener('click', this.onDocEvent, true);
      document.addEventListener('change', this.onDocEvent, true);
      document.addEventListener('visibilitychange', this.onVisibility);
      reducedMotion.addEventListener('change', this.onMotionChange);
    }

    detachDocument() {
      if (!this._docBound) return;
      this._docBound = false;
      document.removeEventListener('click', this.onDocEvent, true);
      document.removeEventListener('change', this.onDocEvent, true);
      document.removeEventListener('visibilitychange', this.onVisibility);
      reducedMotion.removeEventListener('change', this.onMotionChange);
    }

    bindInternal() {
      this.thumbsBox?.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('[data-thumb]') : null;
        if (!button) return;
        const item = this.byId.get(Number(button.getAttribute('data-id')));
        if (item) this.manualShow(item);
      });

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.manualStep(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.manualStep(1));
      this.playButton?.addEventListener('click', () => this.togglePlay());

      this.stage?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.manualStep(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.manualStep(1);
        }
      });

      this.bindSwipe();
      this.bindZoom();
    }

    /* ------------------------------------------------------------ colour picker */

    handleColourEvent(event) {
      const input = this.colourInputFrom(event.target);
      if (input) this.chooseColour(input.value);
    }

    colourFieldset() {
      if (!this.colourOption) return null;
      const pickers = document.querySelectorAll('variant-picker');
      for (const picker of pickers) {
        if (picker.closest('product-card')) continue;
        if (this.productId && picker.getAttribute('data-product-id') !== this.productId) continue;
        for (const fieldset of picker.querySelectorAll('fieldset')) {
          const name = (fieldset.getAttribute('data-option-name') || fieldset.querySelector('legend')?.textContent || '').trim().toLowerCase();
          if (name === this.colourOption || name.startsWith(this.colourOption)) return /** @type {HTMLFieldSetElement} */ (fieldset);
        }
      }
      return null;
    }

    colourInputFrom(target) {
      if (!this.colourOption || !(target instanceof Element)) return null;
      let input = target.closest('input[type="radio"]');
      if (!input) input = target.closest('label')?.querySelector('input[type="radio"]') || null;
      if (!(input instanceof HTMLInputElement)) return null;
      const fieldset = this.colourFieldset();
      return fieldset && fieldset.contains(input) ? input : null;
    }

    pickerColour() {
      const checked = this.colourFieldset()?.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    }

    chooseColour(colour) {
      this.stopAutoplay();
      this.chosenColour = colour;
      this.colour = colour;
      this.setAttribute('data-colour', colour);
      this.applyVisibility();
      const own = this.items.find((item) => item.colours.includes(colour));
      const fallback = this.items.find((item) => !item.colours.length);
      this.show(own || fallback || this.current, { announce: true });
      this.updateControls();
    }

    /* ------------------------------------------------------------------ photos */

    visible() {
      if (this.colour) {
        const own = this.items.filter((item) => item.colours.includes(this.colour));
        if (own.length) return [...own, ...this.items.filter((item) => !item.colours.length)];
      }
      return this.items;
    }

    autoplayItems() {
      return this.visible().filter((item) => item.suitable);
    }

    applyVisibility() {
      const visible = this.visible();
      const position = new Map(visible.map((item, index) => [item.id, index]));
      this.thumbItems.forEach((element, id) => {
        const index = position.get(id);
        element.hidden = index === undefined;
        element.style.order = index === undefined ? '' : String(index);
      });
      this.toggleAttribute('data-single', visible.length < 2);
    }

    show(item, options = {}) {
      if (!item) return;
      this.current = item;
      this.setAttribute('data-current-id', String(item.id));
      const token = ++this.token;

      this.thumbItems.forEach((element, id) => {
        const button = element.firstElementChild;
        if (!button) return;
        if (id === item.id) {
          button.setAttribute('aria-current', 'true');
          this.revealThumb(element, options.instant);
        } else {
          button.removeAttribute('aria-current');
        }
      });

      const next = this.layers[1 - this.activeLayer];
      const previous = this.layers[this.activeLayer];
      if (!next || !previous) return;

      const index = this.items.indexOf(item) + 1;
      next.alt = `${this.dataset.title || document.title.split(' – ')[0] || 'Product'} photo ${index}`;
      next.removeAttribute('aria-hidden');
      next.sizes = SIZES;
      next.srcset = srcset(item.src);
      next.src = sized(item.src, 1000);

      const swap = () => {
        if (token !== this.token) return;
        next.classList.add('is-active');
        previous.classList.remove('is-active');
        previous.setAttribute('aria-hidden', 'true');
        previous.alt = '';
        this.activeLayer = 1 - this.activeLayer;
      };

      if (typeof next.decode === 'function') {
        const guard = new Promise((resolve) => setTimeout(resolve, 800));
        Promise.race([next.decode().catch(() => {}), guard]).then(swap);
      } else {
        swap();
      }

      if (options.announce && this.status) {
        const visible = this.visible();
        this.status.setAttribute('aria-live', 'polite');
        this.status.textContent = `Photo ${visible.indexOf(item) + 1} of ${visible.length}`;
      }

      this.updateCaption();
      this.preloadAhead();
    }

    updateCaption() {
      const el = this.caption;
      if (!el) return;
      const shown = (this.current?.colours || []).join(' / ');
      if (!shown) {
        el.hidden = true;
        return;
      }
      const selected = this.chosenColour || this.pickerColour();
      const preview = Boolean(selected) && !(this.current.colours || []).includes(selected);
      el.hidden = false;
      el.dataset.state = preview ? 'preview' : 'selected';
      const label = el.querySelector('[data-caption-label]');
      const name = el.querySelector('[data-caption-shown]');
      const sel = el.querySelector('[data-caption-selected]');
      if (label) label.textContent = preview ? 'Previewing' : 'Colour';
      if (name) name.textContent = shown;
      if (sel) {
        sel.textContent = preview ? `Selected: ${selected}` : '';
        sel.hidden = !preview;
      }
    }

    preloadAhead() {
      const list = this.autoplayItems();
      if (list.length < 2) return;
      const index = list.indexOf(this.current);
      for (let step = 1; step <= 2; step++) {
        const item = list[(index + step + list.length) % list.length];
        if (!item || item === this.current || this.preloaded.has(item.id)) continue;
        this.preloaded.add(item.id);
        const image = new Image();
        image.sizes = SIZES;
        image.srcset = srcset(item.src);
        image.src = sized(item.src, 1000);
      }
    }

    revealThumb(element, instant) {
      const box = this.thumbsBox;
      if (!box || element.hidden) return;
      const boxRect = box.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const vertical = box.scrollHeight > box.clientHeight + 1 && getComputedStyle(box).flexDirection === 'column';
      const behavior = instant || reducedMotion.matches ? 'auto' : 'smooth';
      if (vertical) {
        const offset = rect.top - boxRect.top - (box.clientHeight - rect.height) / 2;
        box.scrollTo({ top: box.scrollTop + offset, behavior });
      } else if (box.scrollWidth > box.clientWidth + 1) {
        const offset = rect.left - boxRect.left - (box.clientWidth - rect.width) / 2;
        box.scrollTo({ left: box.scrollLeft + offset, behavior });
      }
    }

    /* ------------------------------------------------------------- manual input */

    manualShow(item) {
      this.stopAutoplay();
      this.show(item, { announce: true });
    }

    manualStep(direction) {
      const visible = this.visible();
      if (visible.length < 2) return;
      const index = Math.max(0, visible.indexOf(this.current));
      this.manualShow(visible[(index + direction + visible.length) % visible.length]);
    }

    /* ---------------------------------------------------------------- autoplay */

    startAutoplay() {
      if (reducedMotion.matches || this.autoplayItems().length < 2) return false;
      this.playing = true;
      this.setAttribute('data-playing', 'true');
      this.setAttribute('data-mode', 'auto');
      this.status?.setAttribute('aria-live', 'off');
      this.schedule();
      this.updateControls();
      return true;
    }

    stopAutoplay() {
      clearTimeout(this.timer);
      this.timer = 0;
      this.playing = false;
      this.setAttribute('data-playing', 'false');
      this.setAttribute('data-mode', 'manual');
      this.updateControls();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.tick(), this.intervalMs);
    }

    tick() {
      this.timer = 0;
      if (!this.playing) return;
      if (document.hidden || this.dialog?.open || !this.isConnected || !this.getClientRects().length) {
        this.schedule();
        return;
      }
      const list = this.autoplayItems();
      if (list.length < 2) {
        this.stopAutoplay();
        return;
      }
      const index = list.indexOf(this.current);
      this.show(list[(index + 1) % list.length]);
      this.schedule();
    }

    togglePlay() {
      if (this.playing) {
        this.stopAutoplay();
        return;
      }
      if (this.startAutoplay()) this.tick();
    }

    updateControls() {
      const button = this.playButton;
      if (!button) return;
      button.hidden = reducedMotion.matches;
      button.disabled = !this.playing && this.autoplayItems().length < 2;
      const label = this.playing ? 'Stop automatic photo changes' : 'Start automatic photo changes';
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }

    /* Swipe and zoom are added by mv-gallery-ui.js, which extends this class. */
    bindSwipe() {}

    bindZoom() {}
  }

  MvGallery.sized = sized;
  window.MvGalleryBase = MvGallery;

  document.addEventListener('DOMContentLoaded', () => {
    if (!customElements.get('mv-gallery')) customElements.define('mv-gallery', MvGallery);
  });
})();